import { useEffect, useRef, useState } from "react";
import { resolveImageUrl } from "../../utils/ImageUrl";
import { usePublicSiteData } from "./PublicSiteContext";
import { useStage } from "./StagedImageLoading";

const SLIDE_INTERVAL_MS = 5000;

const PublicHero = () => {
  const website = usePublicSiteData();
  const [index, setIndex] = useState(0);

  const images = website?.promoImages ?? [];
  const hasSlides = images.length > 0;

  // Staged loading: the whole "promo" group unlocks the next group after its
  // FIRST image loads, while the slideshow itself only animates once every
  // promo image has finished downloading.
  const { active: promoActive, reportImageLoaded } = useStage(
    "promo",
    images.length,
    1,
  );
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [loadedSlides, setLoadedSlides] = useState(0);
  const [allPromoLoaded, setAllPromoLoaded] = useState(images.length <= 1);
  const reportedSlides = useRef<Set<number>>(new Set());
  const fallbackReported = useRef(false);

  useEffect(() => {
    if (loadedSlides >= images.length && images.length > 0) {
      setAllPromoLoaded(true);
    }
  }, [loadedSlides, images.length]);

  useEffect(() => {
    if (!allPromoLoaded || images.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [allPromoLoaded, images.length]);

  if (!website) return null;

  const org = website.organization;
  const fallbackUrl = org.bannerUrl;

  const handleSlideLoad = (i: number) => {
    if (reportedSlides.current.has(i)) return;
    reportedSlides.current.add(i);
    reportImageLoaded("promo");
    if (i === 0) setFirstLoaded(true);
    setLoadedSlides((n) => n + 1);
  };

  // A cached image can finish loading before React attaches `onLoad`, so also
  // report via the element's `complete` flag when it mounts.
  const reportSlideIfComplete = (el: HTMLImageElement | null, i: number) => {
    if (el && el.complete) handleSlideLoad(i);
  };

  const handleFallbackLoad = () => {
    if (fallbackReported.current) return;
    fallbackReported.current = true;
    reportImageLoaded("promo");
  };

  return (
    <section
      className={`psite-banner ${hasSlides ? "psite-banner--slideshow" : ""}`}
    >
      {hasSlides ? (
        <>
          <div
            className="psite-banner__slides"
            style={{ "--slide-index": index } as React.CSSProperties}
          >
            {images.map((img, i) => {
              // First slide renders immediately; the rest wait until it loads.
              const show = promoActive && (i === 0 || firstLoaded);
              return show ? (
                <img
                  key={`${img.imageUrl}-${i}`}
                  className="psite-banner__slide"
                  src={resolveImageUrl(img.imageUrl)}
                  alt={img.title || ""}
                  ref={(el) => reportSlideIfComplete(el, i)}
                  onLoad={() => handleSlideLoad(i)}
                  onError={() => handleSlideLoad(i)}
                />
              ) : (
                <div
                  key={`${img.imageUrl}-${i}`}
                  className="psite-banner__slide psite-lazy"
                  aria-hidden="true"
                />
              );
            })}
          </div>
          <div className="psite-banner__overlay" />
          {images.length > 1 && (
            <div className="psite-banner__dots" role="tablist" aria-label="Promo slides">
              {images.map((img, i) => (
                <button
                  key={`${img.imageUrl}-${i}`}
                  type="button"
                  className={`psite-banner__dot ${i === index ? "is-active" : ""}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Show slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </>
      ) : fallbackUrl ? (
        <>
          {promoActive ? (
            <img
              className="psite-banner__bg"
              src={resolveImageUrl(fallbackUrl)}
              alt=""
              ref={(el) => {
                if (el && el.complete) handleFallbackLoad();
              }}
              onLoad={handleFallbackLoad}
              onError={handleFallbackLoad}
            />
          ) : (
            <div className="psite-banner__bg psite-lazy" aria-hidden="true" />
          )}
          <div className="psite-banner__overlay" />
        </>
      ) : (
        <div className="psite-banner__content">
          <h1>{org.tagline || "Welcome"}</h1>
          <p>{org.name}</p>
        </div>
      )}
    </section>
  );
};

export default PublicHero;
